import { useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import PropTypes from 'prop-types';

import { getTopicLibraryCardIcon } from '../../utilities/helpers';

import {
  Badge,
  ButtonV2,
  Checkbox,
  CloseIcon,
  FunnelModifiedIcon,
  FunnelSimpleIcon,
  IconButtonV2,
  Input,
  Layout,
  LoaderSkeleton,
  OverlayPopover,
  PlusPlainIcon,
  Popover,
  TopicLibraryCard,
  truncateTextWithEllipsis,
} from '@utilities';

import styles from './_index.module.scss';

const NarrativesTopicsWindow = ({
  activeTopicIds = [],
  availableTags = [],
  hasTagLoadError = false,
  narrativeTopicGroups = null,
  onCloseToolWindow,
  onInsertTopicCard,
  selectedTags = [],
  setSelectedTags = () => {},
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const buttonRef = useRef();
  const isInitialLoading = !Array.isArray(narrativeTopicGroups);

  const activeTopicIdSet = useMemo(
    () => new Set((activeTopicIds || []).filter((id) => id != null).map(String)),
    [activeTopicIds]
  );
  const handleInsertTopic = (group) => {
    if (!group || typeof onInsertTopicCard !== 'function') return;
    onInsertTopicCard(group);
  };

  const normalizedSearchValue = searchValue.trim().toLowerCase();

  const filteredNarrativeTopics = useMemo(
    () =>
      (Array.isArray(narrativeTopicGroups) ? narrativeTopicGroups : []).filter((group) =>
        (group.grouping?.toLowerCase() || '').includes(normalizedSearchValue)
      ),
    [narrativeTopicGroups, normalizedSearchValue]
  );

  const favoriteTopics = useMemo(
    () =>
      filteredNarrativeTopics.filter((group) => group.topics?.some((topic) => topic.isFavorited)),
    [filteredNarrativeTopics]
  );

  const otherTopics = useMemo(
    () =>
      filteredNarrativeTopics.filter((group) => !group.topics?.some((topic) => topic.isFavorited)),
    [filteredNarrativeTopics]
  );

  const handleToggleTag = (value) => {
    setSelectedTags((prevSelectedTags) =>
      prevSelectedTags.includes(value)
        ? prevSelectedTags.filter((tag) => tag !== value)
        : [...prevSelectedTags, value]
    );
  };

  const renderLoadingCards = (cardsToRender) =>
    Array.from({ length: cardsToRender }, (_, index) => (
      <TopicLibraryCard key={`loading-card-${index}`}>
        <LoaderSkeleton height={85}>
          <rect height="75" rx="7" ry="7" width="80px" x="0" y="0" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="5" />
          <rect height="10" rx="7" ry="15" width="65%" x="110" y="25" />
        </LoaderSkeleton>
      </TopicLibraryCard>
    ));

  const renderTopicCards = (groups) =>
    groups
      .filter(
        (group) =>
          Boolean(group?.grouping) && Array.isArray(group?.topics) && group.topics.length > 0
      )
      .map((group) => {
        const topicIds = group.topics
          .map((topic) => topic?.id)
          .filter((id) => id != null)
          .map(String);
        const addedVariants = topicIds.filter((id) => activeTopicIdSet.has(id)).length;
        const totalVariants = group.topics.length;
        const isDisabled = addedVariants >= totalVariants;
        const isActive = addedVariants > 0;
        const representativeTopic = group.topics[0];

        return (
          <TopicLibraryCard
            className={
              isDisabled
                ? styles['narratives-topics-window-topic-library-card-disabled']
                : styles['narratives-topics-window-topic-library-card']
            }
            footer={
              <div className={styles['topic-card-footer']}>
                <ButtonV2
                  className={styles['narratives-topics-window-insert-button']}
                  disabled={isDisabled}
                  icon={isDisabled ? null : <PlusPlainIcon />}
                  onClick={(event) => {
                    event.stopPropagation();
                    handleInsertTopic(group);
                  }}
                  size="small"
                  variant="subtle"
                >
                  {isDisabled ? 'All variants added' : 'Insert'}
                </ButtonV2>
                {totalVariants > 1 && (
                  <Badge
                    label={
                      addedVariants === 0
                        ? `${totalVariants} Variants`
                        : `${addedVariants}/${totalVariants} variants added`
                    }
                  />
                )}
              </div>
            }
            icon={getTopicLibraryCardIcon(representativeTopic.vizType)}
            isActive={isActive}
            isFooterAlwaysVisible
            key={group.grouping}
            title={
              truncateTextWithEllipsis({ length: 80, text: group.grouping }) || 'Untitled Topic'
            }
          />
        );
      });

  return (
    <div className={styles['topics-window']}>
      <div className={styles['topics-window-header-row']}>
        <strong className={styles['topics-window-header']}>Assemble with Topic Cards</strong>
        {onCloseToolWindow ? (
          <IconButtonV2
            aria-label="Close"
            icon={<CloseIcon />}
            onClick={onCloseToolWindow}
            size="medium"
            tooltip="Close"
            tooltipProps={{ placement: 'left' }}
            variant="default"
          />
        ) : null}
      </div>

      <div className={styles['topics-window-search-input-container']}>
        <Input
          aria-label="Find a topic"
          className={styles['topics-window-search-input']}
          data-testid="narratives-topics-search-input"
          onChange={setSearchValue}
          placeholder="Find a topic"
          value={searchValue}
        />
        <IconButtonV2
          aria-label="Filter topics"
          className={classNames(styles['topics-window-filter-button'], {
            [styles['is-active']]: isPopoverOpen,
          })}
          disabled={hasTagLoadError || availableTags.length === 0}
          icon={selectedTags.length > 0 ? <FunnelModifiedIcon /> : <FunnelSimpleIcon />}
          onClick={() => setIsPopoverOpen((prev) => !prev)}
          ref={buttonRef}
          size="small"
          tooltip={
            hasTagLoadError && 'Tags are unavailable right now. Please refresh and try again.'
          }
          tooltipProps={{ placement: 'bottom' }}
          variant="subtle"
        />
        <OverlayPopover
          className={styles['topics-window-popover']}
          header="FILTER BY TAG"
          isActive={isPopoverOpen}
          onHide={() => setIsPopoverOpen(false)}
          target={buttonRef?.current}
        >
          <Layout.Flex
            className={styles['topics-window-popover-content']}
            direction="column"
            gap="small"
          >
            {availableTags.map((tag) => (
              <Popover.Item as="div" key={tag.value}>
                <Checkbox
                  id={`narratives-topics-filter-${tag.value}`}
                  isChecked={selectedTags.includes(tag.value)}
                  label={tag.label}
                  onChange={handleToggleTag}
                  value={tag.value}
                />
              </Popover.Item>
            ))}
          </Layout.Flex>
        </OverlayPopover>
      </div>

      <div className={styles['topics-window-topic-library']}>
        {isInitialLoading ? (
          <>
            {renderLoadingCards(3)}
            {renderLoadingCards(6)}
          </>
        ) : (
          <>
            {favoriteTopics.length > 0 && (
              <>
                <h4 className={styles['topics-window-section-title']}>Favorite Topic Cards</h4>
                {renderTopicCards(favoriteTopics)}
              </>
            )}

            <h4 className={styles['topics-window-section-title']}>
              {favoriteTopics.length > 0 ? 'Other Topic Cards' : 'Topic Cards'}
            </h4>
            {renderTopicCards(otherTopics)}
          </>
        )}
        {!isInitialLoading && filteredNarrativeTopics.length === 0 && (
          <div className={styles['topics-window-no-results']}>No topic cards found.</div>
        )}
      </div>
    </div>
  );
};

NarrativesTopicsWindow.propTypes = {
  activeTopicIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
  availableTags: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  hasTagLoadError: PropTypes.bool,
  narrativeTopicGroups: PropTypes.oneOfType([
    PropTypes.oneOf([null]),
    PropTypes.arrayOf(
      PropTypes.shape({
        grouping: PropTypes.string,
        tags: PropTypes.arrayOf(PropTypes.string),
        topics: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
            isFavorited: PropTypes.bool,
            name: PropTypes.string,
            tags: PropTypes.arrayOf(PropTypes.string),
            variant: PropTypes.object,
            vizType: PropTypes.string,
          })
        ),
      })
    ),
  ]),
  onCloseToolWindow: PropTypes.func,
  onInsertTopicCard: PropTypes.func,
  selectedTags: PropTypes.arrayOf(PropTypes.string),
  setSelectedTags: PropTypes.func,
};

export default NarrativesTopicsWindow;
